import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ExportAccountTransactionsFormat, useAccountByIdOrNumberQuery, useIsUserAuthenticatedQuery, } from "generated/generated-models";
import { CDate } from "../../../common/CapitalDate";
import { CError } from "app/common/CError";
import { Loading } from "app/lib/components/Loading";
import { DataGridPro, GridLogicOperator, } from "@mui/x-data-grid-pro";
import { IsNullOrWhiteSpace } from "core/util/stringExtensions";
import TransactionPageIconMoney from "app/components/TransactionPageIconMoney";
import MoneySigned from "app/components/MoneySigned";
import { makeNonEmpty } from "core/util/array";
import { clientConfigFn } from "clientConfig";
import dayjs from "dayjs";
import { DesktopTransactionToolbar } from "./DesktopTransactionToolbar";
import { useMemo, useState } from "react";
import { ExportDateRangeModal } from "./ExportDateRangeModal";
import NoRowsOverlay from "app/components/NoRowsOverlay";
import LaunchIcon from "@mui/icons-material/Launch";
import { Link } from "react-router-dom";
import { IconButton, Tooltip } from "@mui/material";
import { isSameOrAfter, isSameOrBefore } from "core/util/dateExtensions";
const DesktopTransactionList = ({ accountId, accountNumber, }) => {
    const { data: dataUserSession } = useIsUserAuthenticatedQuery();
    const [dateRange, setDateRange] = useState();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [exportFormat, setExportFormat] = useState(ExportAccountTransactionsFormat.Ofx);
    const handleMenuItemClick = (format) => {
        setIsModalOpen(true);
        setExportFormat(format);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
    };
    const { transactionPageSize, transactionHistoryLimitDays = 0 } = clientConfigFn();
    const { data: dataAccountById, loading: loadingAccountById, error: errorAccountById, } = useAccountByIdOrNumberQuery({
        variables: { idOrNumber: accountNumber },
        fetchPolicy: "network-only",
        skip: accountNumber == null,
    });
    const filteredData = useMemo(() => {
        const cutOffDate = transactionHistoryLimitDays > 0
            ? dayjs().add(-transactionHistoryLimitDays, "day").toDate()
            : new Date(1990, 1, 1);
        return makeNonEmpty(dataAccountById?.accountByIdOrNumber?.Transactions)
            .filter((tran) => (tran.IsPending && !tran.WasReversed) || // Pending transactions will be reversed once no longer pending
            (tran.AppearsOnStatement && !tran.WasBackedOut))
            .filter((tran) => new Date(tran.EffectiveDate) >= cutOffDate);
    }, [
        dataAccountById?.accountByIdOrNumber?.Transactions,
        transactionHistoryLimitDays,
    ]);
    const filteredDateData = useMemo(() => {
        if (!dateRange || dateRange[0] == null || dateRange[1] == null) {
            return null;
        }
        const startDate = dateRange[0];
        const endDate = dateRange[1];
        return filteredData.filter((x) => {
            const effectiveDate = dayjs(x.EffectiveDate);
            return (isSameOrAfter(effectiveDate, startDate) &&
                isSameOrBefore(effectiveDate, endDate));
        });
    }, [filteredData, dateRange]);
    if (loadingAccountById) {
        return _jsx(Loading, {});
    }
    if (errorAccountById) {
        return (_jsx("section", { className: "sub-wrap", children: _jsx(CError, { error: "Could not retrieve account transactions." }) }));
    }
    const handleDateChange = (dateRange) => {
        setDateRange(dateRange);
    };
    const allowViewBatchPayments = dataUserSession?.userSession &&
        dataUserSession?.userSession?.AllowViewBatchPayments;
    const columns = [
        {
            field: "Id",
            headerName: "Id",
            width: 0,
            filterable: false,
        },
        {
            field: "EffectiveDate",
            headerName: "Date",
            minWidth: 100,
            flex: 2,
            renderCell: (params) => _jsx(CDate, { date: params.value }),
        },
        {
            field: "TransactionType",
            headerName: "Description",
            minWidth: 280,
            renderCell: (params) => !IsNullOrWhiteSpace(params.row.Note) ? (_jsxs("span", { children: [params.value, _jsx("br", {}), _jsx("b", { children: "Note:" }), " ", params.row.Note] })) : (_jsx("span", { children: params.value })),
        },
        {
            field: "Increase",
            headerName: "Increase",
            minWidth: 100,
            flex: 2,
            renderCell: (params) => (_jsx(TransactionPageIconMoney, { amount: params.value, direction: "debit", isShowZero: false })),
        },
        {
            field: "Decrease",
            headerName: "Decrease",
            minWidth: 100,
            flex: 2,
            renderCell: (params) => (_jsx(TransactionPageIconMoney, { amount: params.value, direction: "credit", isShowZero: false })),
        },
        {
            field: "Balance",
            headerName: "Balance",
            minWidth: 100,
            flex: 2,
            renderCell: (params) => (_jsx(MoneySigned, { amount: params.value, direction: "total" })),
        },
        {
            field: "BatchPaymentId",
            headerName: "",
            minWidth: 10,
            flex: 2,
            renderCell: (params) => {
                if (params.row.BatchPaymentId && allowViewBatchPayments) {
                    return (_jsx(Link, { to: `/BatchPayments/${params.row.BatchPaymentId}`, style: {
                            display: "flex",
                            justifyContent: "flex-end",
                            paddingRight: 30,
                        }, children: _jsx(Tooltip, { title: "View Batch Payment Details", placement: "right", children: _jsx(IconButton, { children: _jsx(LaunchIcon, { sx: { fontSize: "1.25rem" } }) }) }) }));
                }
                return _jsx(_Fragment, {});
            },
        },
    ];
    return (_jsxs(_Fragment, { children: [_jsx(DataGridPro, { autoHeight: true, className: "custom-grid", columns: columns, rows: filteredDateData ?? filteredData, getRowId: (r) => r.Id, pagination: true, pageSizeOptions: [10, 25, 50], initialState: {
                    pagination: {
                        paginationModel: {
                            page: 0,
                            pageSize: transactionPageSize,
                        },
                    },
                }, columnVisibilityModel: {
                    Id: false,
                }, slots: {
                    noRowsOverlay: () => (_jsx(NoRowsOverlay, { children: "There are no transactions for this account." })),
                    toolbar: () => (_jsx(DesktopTransactionToolbar, { handleMenuItemClick: handleMenuItemClick, handleDateChange: handleDateChange, dateRangeValue: dateRange || [null, null] })),
                }, disableColumnSelector: true, slotProps: {
                    filterPanel: {
                        logicOperators: [GridLogicOperator.And],
                        columnsSort: "asc",
                        filterFormProps: {
                            logicOperatorInputProps: {
                                variant: "outlined",
                                size: "small",
                            },
                            columnInputProps: {
                                variant: "outlined",
                                size: "small",
                                sx: { mt: "auto" },
                            },
                            operatorInputProps: {
                                variant: "outlined",
                                size: "small",
                                sx: { mt: "auto" },
                            },
                            deleteIconProps: {
                                sx: {
                                    "& .MuiSvgIcon-root": {
                                        color: "#d32f2f",
                                    },
                                },
                            },
                        },
                        sx: {
                            "& .MuiDataGrid-filterForm": { p: 2 },
                            "& .MuiDataGrid-filterForm:nth-child(even)": {
                                backgroundColor: (theme) => theme.palette.mode === "dark"
                                    ? "#444"
                                    : "#f5f5f5",
                            },
                            "& .MuiDataGrid-filterFormLinkOperatorInput": {
                                mr: 2,
                            },
                            "& .MuiDataGrid-filterFormColumnInput": {
                                mr: 2,
                                width: 150,
                            },
                            "& .MuiDataGrid-filterFormOperatorInput": {
                                mr: 2,
                            },
                            "& .MuiDataGrid-filterFormValueInput": {
                                width: 200,
                            },
                        },
                    },
                } }), isModalOpen && (_jsx(ExportDateRangeModal, { accountId: accountId, open: isModalOpen, onClose: handleCloseModal, exportFormat: exportFormat }))] }));
};
export default DesktopTransactionList;
